// Phase 2 P2-7 — useDeskTrackRecord hook (Public Track Record).
//
// Pulls THE DESK's headline platform metrics + per-formula track record
// + most recent immutable audit-ledger entries.
//
// Backed by:
//   - desk_platform_track_record   (view: 30d platform-wide win rate, CLV hit, abstentions)
//   - desk_formula_track_record    (view: per-formula 30/90d publication + win counts)
//   - desk_audit_ledger            (table: append-only mirrored desk_picks/formulas/outcomes)
//
// All three are read-only. Authenticated users have SELECT via the
// desk_rls_authenticated_read migration.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface DeskPlatformTrackRecord {
  picks_30d: number | null;
  abstentions_30d: number | null;
  platform_win_rate_30d: number | null;
  platform_clv_hit_rate_30d: number | null;
  active_agents_30d: number | null;
  max_conviction_picks_30d: number | null;
}

export interface DeskFormulaTrackRecord {
  formula_name:
    | "standing_ten"
    | "chaos_day"
    | "unseen"
    | "the_anchor"
    | "the_contrarian";
  published_30d: number | null;
  published_90d: number | null;
  wins_30d: number | null;
  win_rate_30d: number | null;
  avg_odds_30d: number | null;
  total_legs_beat_clv_30d: number | null;
  total_legs_settled_30d: number | null;
}

export interface DeskAuditLedgerRow {
  id: string;
  ledger_date: string;
  entry_kind:
    | "desk_pick"
    | "desk_formula"
    | "signal_card"
    | "spend"
    | "clv_outcome"
    | "correction";
  entry_ref_id: string;
  entry_table: string;
  payload: Record<string, unknown>;
  correction_of: string | null;
  created_at: string;
  immutable_at: string | null;
}

export interface DeskTrackRecordState {
  platform: DeskPlatformTrackRecord | null;
  formulas: DeskFormulaTrackRecord[];
  ledger: DeskAuditLedgerRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDeskTrackRecord(): DeskTrackRecordState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [platform, setPlatform] = useState<DeskPlatformTrackRecord | null>(null);
  const [formulas, setFormulas] = useState<DeskFormulaTrackRecord[]>([]);
  const [ledger, setLedger] = useState<DeskAuditLedgerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setPlatform(null);
      setFormulas([]);
      setLedger([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Generated Database types don't include these new views; cast to any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;

    const [platRes, formRes, ledgerRes] = await Promise.all([
      sb.from("desk_platform_track_record").select("*").maybeSingle(),
      sb.from("desk_formula_track_record").select("*"),
      sb
        .from("desk_audit_ledger")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50),
    ]);

    const errs = [platRes.error, formRes.error, ledgerRes.error]
      .filter((e: { message?: string } | null): e is { message: string } => Boolean(e && e.message))
      .map((e) => e.message);
    if (errs.length > 0) setError(errs.join(" · "));

    setPlatform((platRes.data as DeskPlatformTrackRecord | null) ?? null);
    setFormulas((formRes.data ?? []) as DeskFormulaTrackRecord[]);
    setLedger((ledgerRes.data ?? []) as DeskAuditLedgerRow[]);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<DeskTrackRecordState>(
    () => ({ platform, formulas, ledger, loading, error, refetch }),
    [platform, formulas, ledger, loading, error, refetch],
  );
}
